import { useRef, useState } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Badge,
  Progress,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  SimpleGrid,
  Spinner,
  Flex,
  Button,
  ButtonGroup,
  useToast,
  useDisclosure,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '../hooks/useSubscription';
import {
  useCreateCheckout,
  useUpgradePlan,
  useDowngradePlan,
  useCancelSubscription,
  useReactivateSubscription,
  useCancelDowngrade,
} from '../hooks/useBilling';
import type { BillingCycle } from '../api/billingApi';
import type { SubscriptionPlanInfo } from '../api/subscriptionApi';

type PlanAction = 'subscribe' | 'upgrade' | 'downgrade' | 'current';

function formatPrice(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(amount);
  } catch {
    return `${amount} ${currency}`;
  }
}

function errMessage(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

export function SubscriptionPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const { subscription, plans, usage, isLoading, isTrialing, daysRemaining, accessStatus, currentPlan } =
    useSubscription();

  const [cycle, setCycle] = useState<BillingCycle>('monthly');
  const [busyPlan, setBusyPlan] = useState<string | null>(null);

  const checkout = useCreateCheckout();
  const upgrade = useUpgradePlan();
  const downgrade = useDowngradePlan();
  const cancel = useCancelSubscription();
  const reactivate = useReactivateSubscription();
  const cancelDowngrade = useCancelDowngrade();

  const cancelRef = useRef<HTMLButtonElement>(null);
  const { isOpen: isCancelOpen, onOpen: onCancelOpen, onClose: onCancelClose } = useDisclosure();

  // An org has a paid subscription once the state machine marks it active. Before
  // that (trialing / grace / locked / expired) the path to a plan is checkout;
  // after it, plan changes go through upgrade/downgrade (see frontend-checkout-scope.md).
  const isActivePaid = subscription?.status === 'active';

  const statusKey =
    accessStatus === 'locked'
      ? 'locked'
      : accessStatus === 'restricted'
        ? 'grace'
        : isTrialing
          ? 'trialing'
          : 'active';
  const statusColor =
    statusKey === 'locked' ? 'red' : statusKey === 'grace' ? 'orange' : statusKey === 'trialing' ? 'blue' : 'green';

  const sortedPlans = [...plans].sort((a, b) => a.displayOrder - b.displayOrder);
  const currentDisplayOrder = plans.find((p) => p.id === currentPlan)?.displayOrder ?? -1;

  const planLabel = (id?: string) => (id ? t(`subscription.plans.${id}.name`, id) : '');
  const fmtDate = (d?: string) => (d ? new Date(d).toLocaleDateString() : '');

  function planActionFor(plan: SubscriptionPlanInfo): PlanAction {
    if (!isActivePaid) return 'subscribe';
    if (plan.id === currentPlan) return 'current';
    return plan.displayOrder > currentDisplayOrder ? 'upgrade' : 'downgrade';
  }

  async function handlePlanAction(plan: SubscriptionPlanInfo, action: PlanAction) {
    if (action === 'current') return;
    setBusyPlan(plan.id);
    try {
      if (action === 'subscribe') {
        const origin = window.location.origin;
        const { redirectUrl } = await checkout.mutateAsync({
          plan: plan.id,
          billingCycle: cycle,
          successUrl: `${origin}/billing/success`,
          cancelUrl: `${origin}/billing/cancel`,
        });
        window.location.href = redirectUrl;
        return; // navigating away; leave busy state set until unload
      }
      if (action === 'upgrade') {
        await upgrade.mutateAsync({ plan: plan.id, billingCycle: cycle });
        toast({ status: 'success', title: t('subscription.toast.upgraded'), duration: 4000, isClosable: true });
      } else {
        await downgrade.mutateAsync({ plan: plan.id, billingCycle: cycle });
        toast({ status: 'success', title: t('subscription.toast.downgradeScheduled'), duration: 4000, isClosable: true });
      }
    } catch (e) {
      toast({
        status: 'error',
        title: t('subscription.toast.actionError'),
        description: errMessage(e),
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setBusyPlan(null);
    }
  }

  async function confirmCancel() {
    try {
      await cancel.mutateAsync();
      toast({ status: 'success', title: t('subscription.toast.cancelScheduled'), duration: 4000, isClosable: true });
    } catch (e) {
      toast({ status: 'error', title: t('subscription.toast.actionError'), description: errMessage(e), duration: 6000, isClosable: true });
    } finally {
      onCancelClose();
    }
  }

  async function runManage(fn: () => Promise<unknown>, successKey: string) {
    try {
      await fn();
      toast({ status: 'success', title: t(successKey), duration: 4000, isClosable: true });
    } catch (e) {
      toast({ status: 'error', title: t('subscription.toast.actionError'), description: errMessage(e), duration: 6000, isClosable: true });
    }
  }

  return (
    <Box maxW="900px">
      <Heading size="lg" color="gray.800" mb={4}>
        {t('subscription.title')}
      </Heading>

      {isLoading ? (
        <Flex justify="center" py={16}>
          <Spinner size="xl" color="brand.500" thickness="3px" />
        </Flex>
      ) : (
        <Tabs colorScheme="brand">
          <TabList>
            <Tab>{t('subscription.myPlan')}</Tab>
            <Tab>{t('subscription.allPlans')}</Tab>
          </TabList>
          <TabPanels>
            <TabPanel px={0}>
              <Box borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={5} bg="white" mb={6}>
                <HStack justify="space-between" mb={2} wrap="wrap">
                  <Heading size="md" color="gray.800" textTransform="capitalize">
                    {currentPlan ?? t('subscription.noPlan')}
                  </Heading>
                  <Badge colorScheme={statusColor} px={2} py={1} borderRadius="md">
                    {t(`subscription.status.${statusKey}`)}
                  </Badge>
                </HStack>
                {isTrialing && (
                  <Text color="gray.500" fontSize="sm">
                    {t('subscription.trialDaysLeft', { days: daysRemaining })}
                  </Text>
                )}
                {subscription?.currentPeriodEnd && !isTrialing && !subscription?.cancelAtPeriodEnd && (
                  <Text color="gray.500" fontSize="sm">
                    {t('subscription.renewsOn', { date: fmtDate(subscription.currentPeriodEnd) })}
                  </Text>
                )}

                {/* Subscription management — cancel / reactivate / pending downgrade (US6/US4). */}
                {isActivePaid && (
                  <Box mt={4} pt={4} borderTopWidth="1px" borderColor="gray.100">
                    {subscription?.pendingPlan ? (
                      <HStack justify="space-between" wrap="wrap" gap={2}>
                        <Text fontSize="sm" color="gray.600">
                          {t('subscription.manage.downgradeScheduled', {
                            plan: planLabel(subscription.pendingPlan),
                            date: fmtDate(subscription.currentPeriodEnd),
                          })}
                        </Text>
                        <Button
                          size="sm"
                          variant="outline"
                          isLoading={cancelDowngrade.isPending}
                          onClick={() =>
                            runManage(() => cancelDowngrade.mutateAsync(), 'subscription.toast.downgradeCancelled')
                          }
                        >
                          {t('subscription.manage.cancelDowngrade')}
                        </Button>
                      </HStack>
                    ) : subscription?.cancelAtPeriodEnd ? (
                      <HStack justify="space-between" wrap="wrap" gap={2}>
                        <Text fontSize="sm" color="orange.600">
                          {t('subscription.manage.cancelsOn', { date: fmtDate(subscription.currentPeriodEnd) })}
                        </Text>
                        <Button
                          size="sm"
                          colorScheme="brand"
                          isLoading={reactivate.isPending}
                          onClick={() => runManage(() => reactivate.mutateAsync(), 'subscription.toast.reactivated')}
                        >
                          {t('subscription.manage.reactivate')}
                        </Button>
                      </HStack>
                    ) : (
                      <HStack justify="space-between" wrap="wrap" gap={2}>
                        <Text fontSize="sm" color="gray.600">
                          {t('subscription.manage.manageTitle')}
                        </Text>
                        <Button size="sm" variant="outline" colorScheme="red" onClick={onCancelOpen}>
                          {t('subscription.manage.cancel')}
                        </Button>
                      </HStack>
                    )}
                  </Box>
                )}
              </Box>

              <Heading size="sm" color="gray.700" mb={3}>
                {t('subscription.usage')}
              </Heading>
              {usage.length === 0 ? (
                <Text color="gray.400" fontSize="sm">
                  {t('subscription.noUsage')}
                </Text>
              ) : (
                <VStack align="stretch" spacing={4}>
                  {usage.map((q) => (
                    <Box key={q.quotaType}>
                      <HStack justify="space-between" mb={1}>
                        <Text fontSize="sm" color="gray.700">
                          {t(`subscription.quotas.${q.quotaType}`, q.quotaType)}
                        </Text>
                        <Text fontSize="sm" color={q.atLimit ? 'red.500' : 'gray.500'}>
                          {q.current} / {q.effectiveLimit}
                        </Text>
                      </HStack>
                      <Progress
                        value={Math.min(100, q.percentUsed)}
                        size="sm"
                        borderRadius="full"
                        colorScheme={q.atLimit ? 'red' : q.nearLimit ? 'orange' : 'brand'}
                      />
                    </Box>
                  ))}
                </VStack>
              )}
            </TabPanel>

            <TabPanel px={0}>
              <Flex justify="center" mb={5}>
                <ButtonGroup isAttached size="sm" variant="outline">
                  <Button
                    onClick={() => setCycle('monthly')}
                    colorScheme={cycle === 'monthly' ? 'brand' : 'gray'}
                    variant={cycle === 'monthly' ? 'solid' : 'outline'}
                  >
                    {t('subscription.cycle.monthly')}
                  </Button>
                  <Button
                    onClick={() => setCycle('annual')}
                    colorScheme={cycle === 'annual' ? 'brand' : 'gray'}
                    variant={cycle === 'annual' ? 'solid' : 'outline'}
                  >
                    {t('subscription.cycle.annual')}
                  </Button>
                </ButtonGroup>
              </Flex>
              <SimpleGrid columns={{ base: 1, md: sortedPlans.length >= 3 ? 3 : 2 }} spacing={4}>
                {sortedPlans.map((plan) => {
                  const action = planActionFor(plan);
                  return (
                    <PlanCard
                      key={plan.id}
                      plan={plan}
                      cycle={cycle}
                      action={action}
                      isBusy={busyPlan === plan.id}
                      onAction={handlePlanAction}
                    />
                  );
                })}
              </SimpleGrid>
            </TabPanel>
          </TabPanels>
        </Tabs>
      )}

      <AlertDialog isOpen={isCancelOpen} leastDestructiveRef={cancelRef} onClose={onCancelClose} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('subscription.manage.confirmCancelTitle')}
            </AlertDialogHeader>
            <AlertDialogBody>{t('subscription.manage.confirmCancelBody')}</AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} variant="ghost" onClick={onCancelClose}>
                {t('subscription.manage.keepPlan')}
              </Button>
              <Button colorScheme="red" ml={3} onClick={confirmCancel} isLoading={cancel.isPending}>
                {t('subscription.manage.confirmCancel')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}

function PlanCard({
  plan,
  cycle,
  action,
  isBusy,
  onAction,
}: {
  plan: SubscriptionPlanInfo;
  cycle: BillingCycle;
  action: PlanAction;
  isBusy: boolean;
  onAction: (plan: SubscriptionPlanInfo, action: PlanAction) => void;
}) {
  const { t } = useTranslation();
  const isCurrent = action === 'current';
  const price = cycle === 'annual' ? plan.priceAnnual : plan.priceMonthly;
  const perLabel = cycle === 'annual' ? t('subscription.perYear') : t('subscription.perMonth');

  return (
    <Box
      borderWidth="2px"
      borderColor={isCurrent ? 'brand.500' : plan.recommended ? 'brand.200' : 'gray.200'}
      borderRadius="lg"
      p={5}
      bg="white"
      position="relative"
      display="flex"
      flexDirection="column"
    >
      <HStack justify="space-between" mb={1}>
        <Heading size="md" color="gray.800">
          {plan.name}
        </Heading>
        {isCurrent ? (
          <Badge colorScheme="brand">{t('subscription.current')}</Badge>
        ) : plan.recommended ? (
          <Badge colorScheme="purple">{t('subscription.recommended')}</Badge>
        ) : null}
      </HStack>
      <HStack align="baseline" mb={3}>
        <Text fontSize="2xl" fontWeight="800" color="gray.800">
          {formatPrice(price, plan.currency)}
        </Text>
        <Text fontSize="sm" color="gray.500">
          {perLabel}
        </Text>
      </HStack>
      {plan.description && (
        <Text fontSize="sm" color="gray.500" mb={3}>
          {plan.description}
        </Text>
      )}
      <VStack align="stretch" spacing={1.5} flex={1}>
        {plan.features.map((f) => (
          <HStack key={f} spacing={2} align="start">
            <Text color="green.500" fontSize="sm">
              ✓
            </Text>
            <Text fontSize="sm" color="gray.600">
              {f}
            </Text>
          </HStack>
        ))}
      </VStack>

      {isCurrent ? (
        <Button mt={4} w="full" variant="outline" isDisabled>
          {t('subscription.actions.current')}
        </Button>
      ) : (
        <Button
          mt={4}
          w="full"
          colorScheme="brand"
          variant={action === 'downgrade' ? 'outline' : 'solid'}
          isLoading={isBusy}
          onClick={() => onAction(plan, action)}
        >
          {t(`subscription.actions.${action}`)}
        </Button>
      )}
    </Box>
  );
}

export default SubscriptionPage;
